import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import useGuardianAcademicService, {
  GuardianAssessment,
  GuardianHomework,
  GuardianAttendance,
  GuardianLibraryBook,
} from '../../../../apis/guardian/sub-apis/useGuardianAcademicService';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  IScheduleListResponse,
  ILessonPlanListResponse,
  IClassListResponse,
  ICourseListResponse,
} from '../../../../apis/academics/academics.interfaces';

type TQueryError = IFHError<{ message: string }>;

export const getGuardianAssessmentsKey = 'get-guardian-assessments';
export const getGuardianHomeworkKey = 'get-guardian-homework';
export const getGuardianAttendanceKey = 'get-guardian-attendance';
export const getGuardianLibraryKey = 'get-guardian-library';
export const getGuardianScheduleKey = 'get-guardian-schedule';
export const getGuardianCoursesClassesKey = 'get-guardian-courses-classes';
export const getGuardianLessonPlansKey = 'get-guardian-lesson-plans';

export const useGuardianAssessmentsHook = (
  studentId: string,
  params: TPaginationParams & { subject?: string; fromDate?: string; toDate?: string; academicYear?: string; grade?: string },
  options?: UseQueryOptions<{ data: GuardianAssessment[]; count: number }, TQueryError>
): UseQueryResult<{ data: GuardianAssessment[]; count: number }, TQueryError> => {
  const { getAssessments } = useGuardianAcademicService();

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 10 };
  return useQuery<{ data: GuardianAssessment[]; count: number }, TQueryError>({
    queryKey: [getGuardianAssessmentsKey, studentId, safeParams],
    queryFn: () => getAssessments(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};

export const useGuardianHomeworkHook = (
  studentId: string,
  params: TPaginationParams & { subject?: string; status?: string; academicYear?: string; grade?: string },
  options?: UseQueryOptions<{ data: GuardianHomework[]; count: number }, TQueryError>
): UseQueryResult<{ data: GuardianHomework[]; count: number }, TQueryError> => {
  const { getHomework } = useGuardianAcademicService();

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 10 };
  return useQuery<{ data: GuardianHomework[]; count: number }, TQueryError>({
    queryKey: [getGuardianHomeworkKey, studentId, safeParams],
    queryFn: () => getHomework(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};

export const useGuardianAttendanceHook = (
  studentId: string,
  params: TPaginationParams & { fromDate?: string; toDate?: string },
  options?: UseQueryOptions<{ data: GuardianAttendance[]; count: number }, TQueryError>
): UseQueryResult<{ data: GuardianAttendance[]; count: number }, TQueryError> => {
  const { getAttendance } = useGuardianAcademicService();

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 10 };
  return useQuery<{ data: GuardianAttendance[]; count: number }, TQueryError>({
    queryKey: [getGuardianAttendanceKey, studentId, safeParams],
    queryFn: () => getAttendance(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};

export const useGuardianLibraryHook = (
  studentId: string,
  params: TPaginationParams & { academicYear?: string; grade?: string },
  options?: UseQueryOptions<{ data: GuardianLibraryBook[]; count: number }, TQueryError>
): UseQueryResult<{ data: GuardianLibraryBook[]; count: number }, TQueryError> => {
  const { getLibraryBooks } = useGuardianAcademicService();

  const safeParams = {
    ...params,
    page: params.page ?? 1,
    limit: params.limit ?? 10,
  };
  return useQuery<{ data: GuardianLibraryBook[]; count: number }, TQueryError>({
    queryKey: [getGuardianLibraryKey, studentId, safeParams],
    queryFn: () => getLibraryBooks(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};

export const useGuardianScheduleHook = (
  studentId: string,
  params: TPaginationParams & { academicYear?: string; term?: string },
  options?: UseQueryOptions<IScheduleListResponse, TQueryError>
): UseQueryResult<IScheduleListResponse, TQueryError> => {
  const { getSchedule } = useGuardianAcademicService();

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 10 };
  return useQuery<IScheduleListResponse, TQueryError>({
    queryKey: [getGuardianScheduleKey, studentId, safeParams],
    queryFn: () => getSchedule(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};

export const useGuardianCoursesClassesHook = (
  studentId: string,
  params: TPaginationParams,
  options?: UseQueryOptions<{ classes: IClassListResponse; courses: ICourseListResponse }, TQueryError>
): UseQueryResult<{ classes: IClassListResponse; courses: ICourseListResponse }, TQueryError> => {
  const { getCoursesAndClasses } = useGuardianAcademicService();

  const safeParams = { page: params.page ?? 1, limit: params.limit ?? 10 };
  return useQuery<{ classes: IClassListResponse; courses: ICourseListResponse }, TQueryError>({
    queryKey: [getGuardianCoursesClassesKey, studentId, safeParams],
    queryFn: () => getCoursesAndClasses(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};

export const useGuardianLessonPlansHook = (
  studentId: string,
  params: TPaginationParams & { courseId?: string; status?: string; academicYear?: string; grade?: string },
  options?: UseQueryOptions<ILessonPlanListResponse, TQueryError>
): UseQueryResult<ILessonPlanListResponse, TQueryError> => {
  const { getLessonPlans } = useGuardianAcademicService();
  // TODO: Naman - Add academicYear, grade filters to guardian lesson-plans API
  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 10 };
  return useQuery<ILessonPlanListResponse, TQueryError>({
    queryKey: [getGuardianLessonPlansKey, studentId, safeParams],
    queryFn: () => getLessonPlans(studentId, safeParams),
    enabled: !!studentId,
    ...options,
  });
};
